import { useEffect, useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon, AlertTriangleIcon } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { getSnapById, castVote, createComment, addReaction, deleteSnap, closeSnap, deleteComment, getAiOpinion } from '@/lib/api';
import { toggleLike } from '@/lib/api/likes';
import { removeVote } from '@/lib/api/votes';
import type { Snap, Comment, CommentCategory } from '@/types/database.types';
import { isPast } from 'date-fns';
import { useAuth } from '@/context/AuthContext';
import { supabase } from '@/lib/supabase';
import DeleteModal from '@/components/DeleteModal';
import SnapHeader from '@/components/snap/SnapHeader';
import SnapStats from '@/components/snap/SnapStats';
import SnapOptions from '@/components/snap/SnapOptions';
import CommentSection from '@/components/snap/CommentSection';
import AiDialog from '@/components/snap/AiDialog';
import RealTimeStats from '@/components/RealTimeStats';
import Button from '@/components/form/Button';
import BackToTopButton from '@/components/BackToTopButton';
import SEO from '@/components/SEO';
import { retryFetch } from '@/utils/api-utils';
import { updateMetaTags, resetMetaTags } from '@/utils/meta-updater';
import { useVoteState } from '@/hooks/useVoteState';
import CashPrizeInfo from '@/components/snap/CashPrizeInfo';

export default function SnapDetails() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [snap, setSnap] = useState<Snap | null>(null);
  const [loading, setLoading] = useState(true);
  const [comments, setComments] = useState<Comment[]>([]);
  const [showAiDialog, setShowAiDialog] = useState(false);
  const [aiOpinion, setAiOpinion] = useState<string | null>(null);
  const [loadingAi, setLoadingAi] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showCommentDeleteModal, setShowCommentDeleteModal] = useState(false);
  const [commentToDelete, setCommentToDelete] = useState<string | null>(null);
  const [viewTracked, setViewTracked] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isAgeVerified, setIsAgeVerified] = useState(false);

  // Initialize vote state hook
  const voteState = useVoteState({
    initialSnap: snap,
    onVoteSuccess: (updatedSnap) => {
      setSnap(updatedSnap);
    }
  });

  const fetchSnapData = async () => {
    if (!id) return;

    try {
      console.log('SnapDetails: Fetching snap data for ID:', id);
      
      // Get user profile to check age verification status
      const { data: profile } = await supabase
        .from('profiles')
        .select('age_verified')
        .eq('user_id', user?.id)
        .single();

      setIsAgeVerified(profile?.age_verified || false);
      
      const snapData = await retryFetch(() => getSnapById(id), 3, 1000);
      console.log('SnapDetails: Snap data fetched successfully:', snapData.title);
      console.log('SnapDetails: Card image URL:', snapData.card_image_url);
      
      // Check if this is a CashSNAP and user is not age verified
      if (snapData.cashsnap && !profile?.age_verified) {
        setError('Age verification required to view CashSNAPs');
        setLoading(false);
        return;
      }
      
      setSnap(snapData);
      voteState.setSnap(snapData);
      setComments(snapData.comments || []);
      setError(null);

      // Update meta tags for social sharing
      if (snapData.card_image_url) {
        updateMetaTags(
          `SnapVote: ${snapData.title}`,
          snapData.description || `Join the discussion and vote on this SNAP with ${snapData.total_votes} votes so far!`,
          snapData.card_image_url,
          `${window.location.origin}/snap/${snapData.id}`
        );
      } else {
        // Use default OG image if no card image is available
        updateMetaTags(
          `SnapVote: ${snapData.title}`,
          snapData.description || `Join the discussion and vote on this SNAP with ${snapData.total_votes} votes so far!`,
          'https://snapvote.ai/og-default.jpg',
          `${window.location.origin}/snap/${snapData.id}`
        );
      }
    } catch (error) {
      if (error instanceof Error) {
        switch (error.message) {
          case 'SNAP not found':
            setError('This SNAP has been deleted');
            break;
          case 'You do not have access to this private SNAP':
            setError('You do not have access to this private SNAP');
            break;
          case 'Age verification required to view CashSNAPs':
            setError('Age verification required to view CashSNAPs');
            break;
          default:
            console.error('Unexpected error while fetching SNAP:', error);
            setError('Failed to load SNAP');
            break;
        }
      } else {
        console.error('Unknown error type:', error);
        setError('Something went wrong');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSnapData();
    
    // Cleanup function to reset meta tags when component unmounts
    return () => {
      resetMetaTags();
    };
  }, [id, navigate]);

  // Track view when SNAP is loaded
  useEffect(() => {
    const trackView = async () => {
      if (id && user?.id && !viewTracked && snap) {
        try {
          await supabase.rpc('track_snap_view', {
            p_snap_id: id,
            p_viewer_id: user.id
          });
          setViewTracked(true);
          // Refresh SNAP data to get updated view count
          await fetchSnapData();
        } catch (error) {
          console.error('Error tracking view:', error);
        }
      }
    };

    trackView();
  }, [id, user?.id, viewTracked, snap]);

  const handleVote = async (optionId: string) => {
    await voteState.handleVote(optionId);
  };

  const handleRemoveVote = async () => {
    await voteState.handleRemoveVote();
  };

  const handleDelete = async () => {
    if (!snap) return;
    try {
      await deleteSnap(snap.id);
      toast.success('SNAP deleted successfully');
      navigate('/dashboard');
    } catch (error) {
      toast.error('Failed to delete SNAP');
    }
  };

  const handleClose = async () => {
    if (!snap) return;
    try {
      await closeSnap(snap.id);
      toast.success('SNAP closed successfully');
      const updatedSnap = await getSnapById(snap.id);
      setSnap(updatedSnap);
      voteState.setSnap(updatedSnap);
    } catch (error) {
      toast.error('Failed to close SNAP');
    }
  };

  const handleAddComment = async (content: string, category: CommentCategory) => {
    if (!snap) return;
    try {
      const newComment = await createComment(snap.id, content, category);
      setComments(prev => [newComment, ...prev]);
      toast.success('Comment added successfully!');
    } catch (error) {
      toast.error('Failed to add comment');
    }
  };

  const handleDeleteComment = async (commentId: string) => {
    setCommentToDelete(commentId);
    setShowCommentDeleteModal(true);
  };

  const confirmDeleteComment = async () => {
    if (!commentToDelete) return;
    try {
      await deleteComment(commentToDelete);
      await fetchSnapData(); // Refresh all data to ensure consistency
      toast.success('Comment deleted successfully');
    } catch (error) {
      toast.error('Failed to delete comment');
    } finally {
      setShowCommentDeleteModal(false);
      setCommentToDelete(null);
    }
  };

  const handleReaction = async (commentId: string, type: 'like' | 'dislike') => {
    try {
      await addReaction(commentId, type);
      const updatedSnap = await getSnapById(snap!.id);
      setComments(updatedSnap.comments || []);
      toast.success('Reaction updated');
    } catch (error) {
      toast.error('Failed to update reaction');
    }
  };

  const handleLike = async () => {
    if (!snap) return;
    try {
      // Use the centralized like handling function
      const { liked, count } = await toggleLike(snap.id);
      
      // Update local state to reflect the like/unlike action
      setSnap(prev => {
        if (!prev) return null;
        return {
          ...prev,
          likes_count: count,
          userLiked: liked
        };
      });
      
      toast.success(liked ? 'SNAP liked' : 'SNAP unliked');
    } catch (error) {
      if (error instanceof Error) {
        toast.error(error.message);
      } else {
        toast.error('Failed to update like');
      }
    }
  };

  const handleAiOpinion = async (selectedModes: CommentCategory[]) => {
    if (!snap || selectedModes.length === 0) {
      toast.error('Please select at least one feedback mode');
      return;
    }

    setLoadingAi(true);
    try {
      const opinion = await getAiOpinion(snap.id, selectedModes);
      setAiOpinion(opinion);
    } catch (error) {
      toast.error('Failed to get AI opinion');
    } finally {
      setLoadingAi(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="bg-white rounded-lg shadow-lg p-8 text-center">
          <AlertTriangleIcon className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-4">{error}</h2>
          {error === 'Age verification required to view CashSNAPs' && (
            <Button
              onClick={() => navigate('/verify-age')}
              variant="primary"
              className="mb-4 bg-yellow-600 hover:bg-yellow-700"
            >
              Verify Age
            </Button>
          )}
          <Button
            onClick={() => navigate('/dashboard')}
            variant="primary"
            icon={<ArrowLeftIcon className="h-5 w-5" />}
          >
            Return to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  const isSnapClosed = snap ? isPast(new Date(snap.ends_at)) : false;
  const absoluteUrl = `${window.location.origin}/snap/${id}`;

  return (
    <div className="max-w-4xl mx-auto">
      <SEO 
        title={snap ? `SnapVote: ${snap.title}` : 'SnapVote'}
        description={snap?.description || 'Join the discussion and vote on this SNAP'}
        image={snap?.card_image_url || 'https://snapvote.ai/og-default.jpg'}
        url={absoluteUrl}
        type="article"
      />
      
      <Button
        onClick={() => navigate('/dashboard')}
        variant="ghost"
        size="sm"
        icon={<ArrowLeftIcon className="h-4 w-4" />}
        className="mb-6 text-gray-600 hover:text-gray-900"
      >
        Back to Dashboard
      </Button>

      {snap && (
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          <SnapHeader
            snap={snap}
            onLike={handleLike}
            onAskAi={() => setShowAiDialog(true)}
            onClose={handleClose}
            onDelete={() => setShowDeleteModal(true)}
            isSnapClosed={isSnapClosed}
          />

          <div className="p-6 bg-gray-50 border-y">
            <RealTimeStats snapId={snap.id} />
          </div>

          {snap.cashsnap && (
            <div className="px-6 pt-6">
              <CashPrizeInfo totalVotes={snap.total_votes} endsAt={snap.ends_at} />
            </div>
          )}

          <SnapStats snap={snap} commentsCount={comments.length} />

          <div className="p-6">
            <SnapOptions
              options={snap.options || []}
              totalVotes={snap.total_votes}
              userVote={snap.userVote}
              onVote={handleVote}
              onRemoveVote={handleRemoveVote}
              isSnapClosed={isSnapClosed}
              winningOptionId={snap.winning_option_id}
              isCashSnap={snap.cashsnap}
            />

            <CommentSection
              comments={comments}
              onAddComment={handleAddComment}
              onDeleteComment={handleDeleteComment}
              onReaction={handleReaction}
              userId={user?.id}
            />
          </div>
        </div>
      )}

      <AiDialog
        isOpen={showAiDialog}
        onClose={() => {
          setShowAiDialog(false);
          setAiOpinion(null);
        }}
        onGetOpinion={handleAiOpinion}
        aiOpinion={aiOpinion}
        loadingAi={loadingAi}
      />

      <DeleteModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDelete}
        title="Delete SNAP"
        message="Are you sure you want to delete this SNAP? This action cannot be undone."
      />

      <DeleteModal
        isOpen={showCommentDeleteModal}
        onClose={() => {
          setShowCommentDeleteModal(false);
          setCommentToDelete(null);
        }}
        onConfirm={confirmDeleteComment}
        title="Delete Comment"
        message="Are you sure you want to delete this comment? This action cannot be undone."
      />

      <BackToTopButton />
    </div>
  );
}